import { Children, createContext, useContext, type ReactNode } from 'react';
import { ArrowDownRightIcon, ArrowUpRightIcon, MinusIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { Sparkline } from './Sparkline';
import { Skeleton } from './Skeleton';

export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  /** Relative change, e.g. 0.124 for +12.4%. */
  delta?: number | null;
  /** Pre-formatted delta text (overrides auto formatting). */
  deltaLabel?: ReactNode;
  /** When lower is better (e.g. opt-outs), flip the colour logic. */
  invert?: boolean;
  caption?: ReactNode;
  trend?: number[];
  loading?: boolean;
  className?: string;
  /** Visual size of the figure. */
  size?: 'md' | 'lg';
}

/** Set by StatGroup: tiles share subgrid rows, so labels, figures and captions line up per row. */
const InGroup = createContext(false);

/**
 * KPI: uppercase label, heavy Commissioner figure (800, tight), coloured delta, optional sparkline.
 * Inside a highlighted StatGroup tile (or any `surface-field`) the figure turns saffron.
 */
export function Stat({
  label,
  value,
  delta,
  deltaLabel,
  invert,
  caption,
  trend,
  loading,
  className,
  size = 'md',
}: StatProps) {
  const inGroup = useContext(InGroup);
  const dir = delta == null ? 0 : delta > 0.0005 ? 1 : delta < -0.0005 ? -1 : 0;
  const good = invert ? dir < 0 : dir > 0;
  // A3: the delta is bold coloured text (no pill), semantic when it means good or bad.
  const toneCls = dir === 0 ? 'text-ink-muted' : good ? 'text-success-ink' : 'text-negative-ink';
  const Icon = dir > 0 ? ArrowUpRightIcon : dir < 0 ? ArrowDownRightIcon : MinusIcon;
  const text =
    deltaLabel ??
    (delta != null
      ? `${delta > 0 ? '+' : ''}${(delta * 100).toLocaleString('el-GR', { maximumFractionDigits: 1 })}%`
      : null);

  return (
    // In a StatGroup the tile is a 3-row subgrid (label / figure / caption) and this wrapper steps
    // aside, so a label that wraps to two lines on a narrow tile no longer pushes its figure below
    // the neighbouring tiles' figures. Standalone: h-full + caption pushed to the bottom.
    <div className={cn(inGroup ? 'contents' : 'flex h-full min-w-0 flex-col gap-2', className)}>
      {/* Labels wrap (up to two lines) instead of being ellipsized on narrow tiles. */}
      <div className="eyebrow line-clamp-2 break-words">{label}</div>
      {/* flex-wrap: on a narrow tile the sparkline drops under the value instead of overlapping it. */}
      <div className="flex flex-wrap content-start items-end justify-between gap-x-3 gap-y-2">
        {loading ? (
          <Skeleton className={size === 'lg' ? 'h-11 w-28' : 'h-8 w-20'} />
        ) : (
          <div
            data-stat-value=""
            className={cn(
              'num min-w-0 max-w-full font-extrabold leading-none tracking-display text-ink',
              // Two tiles per row on phones: a slightly smaller figure keeps «1.234,56 €» inside.
              size === 'lg'
                ? 'text-[1.75rem] sm:text-[2.5rem]'
                : 'text-[1.625rem] sm:text-[2.25rem]',
            )}
          >
            {value}
          </div>
        )}
        {trend && trend.length > 1 && !loading && (
          <Sparkline
            data={trend}
            width={96}
            height={32}
            className={cn('shrink-0', dir < 0 && !invert ? 'text-negative' : 'text-primary')}
          />
        )}
      </div>
      {(text || caption) && !loading && (
        <div className="mt-auto flex min-w-0 items-center gap-2 text-sm">
          {text && (
            <span
              data-stat-delta=""
              className={cn(
                'num inline-flex shrink-0 items-center gap-0.5 text-[0.8125rem] font-extrabold',
                toneCls,
              )}
            >
              <Icon weight="bold" className="size-3" />
              {text}
            </span>
          )}
          {caption && <span className="min-w-0 text-ink-muted">{caption}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * A row of KPI cards (A3: separate white cards on the stone canvas): 2 columns on phones and
 * tablets, up to 5 on desktop. An odd last tile spans the 2-column row instead of leaving an empty
 * cell. `highlight="first"` sets the first tile on the blue brand field with a saffron figure and a
 * tonal star (the one number to look at; at most one field card per row).
 */
export function StatGroup({
  children,
  className,
  columns = 4,
  highlight,
}: {
  children: ReactNode;
  className?: string;
  columns?: 2 | 3 | 4 | 5;
  highlight?: 'first';
}) {
  const cols =
    columns === 5
      ? 'lg:grid-cols-5'
      : columns === 4
        ? 'lg:grid-cols-4'
        : columns === 3
          ? 'lg:grid-cols-3'
          : '';
  let seen = false;
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-3.5',
        '[&>*:last-child:nth-child(odd)]:col-span-2',
        columns === 2 ? '' : 'lg:[&>*:last-child:nth-child(odd)]:col-span-1',
        cols,
        className,
      )}
    >
      <InGroup.Provider value={true}>
        {Children.map(children, (child) => {
          if (!child) return null;
          // The first rendered tile (null children are skipped) is the featured one.
          const featured = highlight === 'first' && !seen;
          seen = true;
          return (
            <div
              data-featured={featured || undefined}
              className={cn(
                'relative row-span-3 grid min-w-0 grid-rows-subgrid gap-y-2 overflow-hidden rounded-lg p-4 sm:p-5',
                featured
                  ? 'surface-field border border-transparent shadow-sm [&_[data-stat-delta]]:text-field-accent-text [&_[data-stat-value]]:text-field-accent'
                  : 'border border-hairline bg-surface shadow-sm',
              )}
            >
              {featured && <StatStar />}
              {child}
            </div>
          );
        })}
      </InGroup.Provider>
    </div>
  );
}

/** Tonal saffron-star watermark in the corner of the featured KPI (as in the A3 mockup). */
function StatStar() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className="pointer-events-none absolute -right-3.5 -top-4 size-[4.5rem] fill-field-tonal"
    >
      <path d="M12 1.6l3.1 6.6 7.2.9-5.3 5 1.4 7.1L12 17.7l-6.4 3.5 1.4-7.1-5.3-5 7.2-.9z" />
    </svg>
  );
}
